import type { TranslationBundle } from '@bms/common';

/** VisitorsPage — the front desk's visitor and visit register. */
export const visitors: TranslationBundle = {
  en: {
    statusExpectedLabel: 'expected',
    statusExpectedHint: 'Registered, not yet arrived',
    statusOnSiteLabel: 'on site',
    statusOnSiteHint: 'The visitor is on site',
    statusCheckedOutLabel: 'checked out',
    statusCheckedOutHint: 'The visit is over — terminal',
    statusCancelledLabel: 'cancelled',
    statusCancelledHint: 'The visit will not happen — terminal',
    statusNoShowLabel: 'no show',
    statusNoShowHint: 'Derived: still expected, and the expected end has passed. Nothing stored it.',
    statusOverstayedLabel: 'overstayed',
    statusOverstayedHint: 'Derived: still on site, and the expected end has passed. Nothing stored it.',

    visitTitle: 'Visit',
    fieldVisitor: 'Visitor',
    fieldCompany: 'Company',
    fieldHost: 'Host',
    fieldPurpose: 'Purpose',
    fieldExpected: 'Expected',
    fieldArrived: 'Arrived',
    fieldLeft: 'Left',
    fieldStatus: 'Status',
    storedAs: 'stored as {{status}}',
    timeRange: '{{from}} → {{until}}',
    cardsIssuedTitle: 'Cards issued against this visit',
    cardsIssuedHint: 'A visitor card is an ordinary access card. A replacement is a second issuance, and the signed window on the card — not the expected end above — controls the door.',
    noCardIssued: 'No card has been issued for this visit.',
    cardRetiredTitle: 'Retired: the UID has been released for the next holder',
    cardRetiredLabel: 'retired',
    neverExpires: 'never expires',

    noSiteTitle: 'No site selected',
    noSiteDescription: 'Choose a site to see who is expected at it.',
    notAvailableTitle: 'Visitors is not available to this account',
    notAvailableDescription: 'It needs the visitors:read permission. Ask a site administrator to grant it.',
    subtitle: 'People expected at the site, and the occasions they are expected for.',
    registerPerson: 'Register a person',
    registerVisit: 'Register a visit',
    tabVisits: 'Visits',
    tabPeople: 'People',
    filterStatusAria: 'Filter by status',
    anyStatus: 'Any status',
    statusExpectedOption: 'Expected',
    statusOnSiteOption: 'On site',
    statusCheckedOutOption: 'Checked out',
    statusCancelledOption: 'Cancelled',
    fromLabel: 'Visits from',
    fromPlaceholder: 'From',
    untilLabel: 'Visits until',
    untilPlaceholder: 'Until',
    todayHint: 'Showing today’s visits, by the site’s own clock. Pick a range to see others.',
    noVisitsTitle: 'No visits in this range',
    noVisitsToday: 'Nothing is expected today.',
    noOneRegistered: 'No one is registered yet',

    issueRequiresWrite: 'Issuing a card requires the cards:write permission',
    defaultVisitLabel: 'this visit',

    toastVisitorUpdated: 'Visitor updated',
    toastVisitorRegistered: 'Visitor registered',
    toastSaveVisitorFailed: 'Failed to save the visitor',
    toastVisitorDenied: 'Visitor denied — revoke any card they still hold',
    toastDenyFailed: 'Failed to deny the visitor',
    toastDenyCleared: 'Deny flag cleared',
    toastClearDenyFailed: 'Failed to clear the deny flag',
    toastErased: 'Erased: {{visits}} visit(s) removed, {{cards}} card record(s) scrubbed',
    toastEraseFailed: 'Failed to erase the visitor',

    toastNeedWindow: 'A visit needs an expected start and an expected end',
    toastEndAfterStart: 'The expected end must be after the expected start',
    toastVisitMoved: 'The visit moved. An issued card keeps its signed validity window — replace and re-encode the card if the visitor needs longer access.',
    toastVisitUpdated: 'Visit updated',
    toastChooseVisitor: 'Choose who is coming',
    toastVisitRegistered: 'Visit registered',
    toastSaveVisitFailed: 'Failed to save the visit',
    toastCheckedIn: '{{name}} checked in',
    defaultVisitorName: 'Visitor',
    toastCheckInFailed: 'Failed to check in',
    toastCheckedOutWithCards_one: 'Checked out — {{count}} card revoked',
    toastCheckedOutWithCards_other: 'Checked out — {{count}} cards revoked',
    toastCheckedOut: 'Checked out',
    toastCheckOutFailed: 'Failed to check out',
    toastVisitCancelled: 'Visit cancelled',
    toastCancelFailed: 'Failed to cancel the visit',

    unknownVisitor: 'Unknown',
    hostPrefix: 'Host: {{name}}',
    checkIn: 'Check in',
    checkOut: 'Check out',
    issueVisitorCard: 'Issue visitor card',
    editVisitAria: 'Edit visit',
    cancelAction: 'Cancel',

    deniedBadgeTitle: 'Blocks new visits and new card issuances. It revokes nothing.',
    deniedBadgeLabel: 'denied',
    visitCount_one: '{{count}} visit',
    visitCount_other: '{{count}} visits',
    editPersonAria: 'Edit person',
    allowAction: 'Allow',
    denyAction: 'Deny',
    erasePersonAria: 'Erase person',

    checkOutTitle: 'Check {{name}} out?',
    checkOutDefaultName: 'this visitor',
    checkOutConfirmLabel: 'Check out and revoke their cards',
    checkOutBody1: 'Every card still live on this visit is <0>revoked</0> immediately and every gateway is told, so the card stops opening doors even where there is no connectivity.',
    checkOutBody2: 'The card records are <0>not</0> retired. Retiring is what releases the UID for the next holder, and the desk does that when it hands the card out again — which is what keeps this visitor’s access in the audit trail.',
    cancelVisitTitle: 'Cancel this visit?',
    cancelVisitConfirmLabel: 'Cancel the visit',
    cancelVisitBody: '{{name}} will not be expected. Cancelling is refused once a card has been issued — check the visitor out instead.',
    cancelVisitDefaultName: 'The visitor',
    denyTitle: 'Deny {{name}}?',
    denyConfirmLabel: 'Deny — blocks new visits only',
    denyReasonPlaceholder: 'Reason (optional)',
    denyBody1: 'New visits and new card issuances will be refused for this person.',
    denyBody2: '<0>This revokes nothing.</0> A card already in this person’s hand keeps working. If they hold one, revoke it from <1>Cards</1> as well — the deny flag is never read at a door.',
    eraseTitle: 'Erase {{name}}?',
    eraseConfirmLabel: 'Erase',
    eraseBody: 'Their visits are deleted and their name is scrubbed from the retired card records. Door and card history is kept. It is refused while they hold a card that has not been retired.',
    confirmBack: 'Back',

    editPersonTitle: 'Edit person',
    registerPersonTitle: 'Register a person',
    personDialogDescription: 'A visitor is the person; a visit is one bounded occasion they are expected for.',
    fullNameLabel: 'Full name',
    fullNamePlaceholder: 'Ana Horvat',
    companyLabel: 'Company',
    companyPlaceholder: 'Contracting firm or employer',
    phoneLabel: 'Phone',
    emailLabel: 'Email',
    idRefLabel: 'Identity check reference',
    idRefPlaceholder: 'e.g. checked against passport, ref 2026-0912',
    idRefHint: 'A reference to a check performed at the desk — never a document number, image or scan. It is personal data and is removed when the person is erased.',
    notesLabel: 'Notes',
    registerAction: 'Register',

    editVisitTitle: 'Edit visit',
    registerVisitTitle: 'Register a visit',
    editVisitDescription: 'Changing these does not change a card that has already been issued: the card carries its own signed window.',
    registerVisitDescription: 'A card issued for this visit takes these times as its signed validity window unless the desk narrows it.',
    visitorLabel: 'Visitor',
    whoIsComingPlaceholder: 'Who is coming?',
    deniedSuffix: ' (denied)',
    hostLabel: 'Host',
    hostPlaceholder: 'Who is expecting them',
    purposeLabel: 'Purpose',
    purposePlaceholder: 'Maintenance, interview, delivery…',
    expectedFromLabel: 'Expected from',
    expectedUntilLabel: 'Expected until',
    overstayingWarning: '<0>This visitor is overstaying.</0> Extending the visit records the new expectation. It does not extend their card.',
  },
  hr: {
    statusExpectedLabel: 'očekivan',
    statusExpectedHint: 'Prijavljen, još nije stigao',
    statusOnSiteLabel: 'na lokaciji',
    statusOnSiteHint: 'Posjetitelj je na lokaciji',
    statusCheckedOutLabel: 'odjavljen',
    statusCheckedOutHint: 'Posjet je završen — konačno',
    statusCancelledLabel: 'otkazan',
    statusCancelledHint: 'Posjet se neće dogoditi — konačno',
    statusNoShowLabel: 'nije se pojavio',
    statusNoShowHint: 'Izvedeno: i dalje se očekuje, a očekivani kraj je prošao. Ništa to nije pohranilo.',
    statusOverstayedLabel: 'predugo zadržavanje',
    statusOverstayedHint: 'Izvedeno: i dalje na lokaciji, a očekivani kraj je prošao. Ništa to nije pohranilo.',

    visitTitle: 'Posjet',
    fieldVisitor: 'Posjetitelj',
    fieldCompany: 'Tvrtka',
    fieldHost: 'Domaćin',
    fieldPurpose: 'Svrha',
    fieldExpected: 'Očekivano',
    fieldArrived: 'Stigao/la',
    fieldLeft: 'Otišao/la',
    fieldStatus: 'Status',
    storedAs: 'pohranjeno kao {{status}}',
    timeRange: '{{from}} → {{until}}',
    cardsIssuedTitle: 'Kartice izdane za ovaj posjet',
    cardsIssuedHint: 'Posjetiteljska kartica je obična pristupna kartica. Zamjena je drugo izdavanje, a potpisano razdoblje na kartici — ne očekivani kraj gore — upravlja vratima.',
    noCardIssued: 'Za ovaj posjet nije izdana nijedna kartica.',
    cardRetiredTitle: 'Umirovljena: UID je oslobođen za sljedećeg nositelja',
    cardRetiredLabel: 'umirovljena',
    neverExpires: 'nikad ne istječe',

    noSiteTitle: 'Nije odabrana lokacija',
    noSiteDescription: 'Odaberite lokaciju da biste vidjeli tko se očekuje.',
    notAvailableTitle: 'Posjetitelji nisu dostupni ovom računu',
    notAvailableDescription: 'Potrebna je ovlast visitors:read. Zamolite administratora lokacije da je dodijeli.',
    subtitle: 'Ljudi koji se očekuju na lokaciji i prigode za koje se očekuju.',
    registerPerson: 'Prijavi osobu',
    registerVisit: 'Prijavi posjet',
    tabVisits: 'Posjeti',
    tabPeople: 'Ljudi',
    filterStatusAria: 'Filtriraj po statusu',
    anyStatus: 'Bilo koji status',
    statusExpectedOption: 'Očekivan',
    statusOnSiteOption: 'Na lokaciji',
    statusCheckedOutOption: 'Odjavljen',
    statusCancelledOption: 'Otkazan',
    fromLabel: 'Posjeti od',
    fromPlaceholder: 'Od',
    untilLabel: 'Posjeti do',
    untilPlaceholder: 'Do',
    todayHint: 'Prikazani su današnji posjeti, prema satu lokacije. Odaberite raspon za ostale.',
    noVisitsTitle: 'Nema posjeta u ovom razdoblju',
    noVisitsToday: 'Danas se ništa ne očekuje.',
    noOneRegistered: 'Još nitko nije prijavljen',

    issueRequiresWrite: 'Izdavanje kartice zahtijeva ovlast cards:write',
    defaultVisitLabel: 'ovaj posjet',

    toastVisitorUpdated: 'Posjetitelj ažuriran',
    toastVisitorRegistered: 'Posjetitelj prijavljen',
    toastSaveVisitorFailed: 'Spremanje posjetitelja nije uspjelo',
    toastVisitorDenied: 'Posjetitelj odbijen — opozovite svaku karticu koju još drži',
    toastDenyFailed: 'Odbijanje posjetitelja nije uspjelo',
    toastDenyCleared: 'Oznaka odbijanja uklonjena',
    toastClearDenyFailed: 'Uklanjanje oznake odbijanja nije uspjelo',
    toastErased: 'Izbrisano: uklonjeno posjeta: {{visits}}, očišćeno zapisa kartica: {{cards}}',
    toastEraseFailed: 'Brisanje posjetitelja nije uspjelo',

    toastNeedWindow: 'Posjet treba očekivani početak i očekivani kraj',
    toastEndAfterStart: 'Očekivani kraj mora biti nakon očekivanog početka',
    toastVisitMoved: 'Posjet je pomaknut. Izdana kartica zadržava svoje potpisano razdoblje valjanosti — zamijenite i ponovno zapišite karticu ako posjetitelj treba dulji pristup.',
    toastVisitUpdated: 'Posjet ažuriran',
    toastChooseVisitor: 'Odaberite tko dolazi',
    toastVisitRegistered: 'Posjet prijavljen',
    toastSaveVisitFailed: 'Spremanje posjeta nije uspjelo',
    toastCheckedIn: '{{name}} prijavljen/a',
    defaultVisitorName: 'Posjetitelj',
    toastCheckInFailed: 'Prijava nije uspjela',
    toastCheckedOutWithCards_one: 'Odjavljen — opozvana {{count}} kartica',
    toastCheckedOutWithCards_few: 'Odjavljen — opozvane {{count}} kartice',
    toastCheckedOutWithCards_other: 'Odjavljen — opozvano {{count}} kartica',
    toastCheckedOut: 'Odjavljen',
    toastCheckOutFailed: 'Odjava nije uspjela',
    toastVisitCancelled: 'Posjet otkazan',
    toastCancelFailed: 'Otkazivanje posjeta nije uspjelo',

    unknownVisitor: 'Nepoznato',
    hostPrefix: 'Domaćin: {{name}}',
    checkIn: 'Prijavi',
    checkOut: 'Odjavi',
    issueVisitorCard: 'Izdaj posjetiteljsku karticu',
    editVisitAria: 'Uredi posjet',
    cancelAction: 'Otkaži',

    deniedBadgeTitle: 'Blokira nove posjete i nova izdavanja kartica. Ništa ne opoziva.',
    deniedBadgeLabel: 'odbijen',
    visitCount_one: '{{count}} posjet',
    visitCount_few: '{{count}} posjeta',
    visitCount_other: '{{count}} posjeta',
    editPersonAria: 'Uredi osobu',
    allowAction: 'Dopusti',
    denyAction: 'Odbij',
    erasePersonAria: 'Izbriši osobu',

    checkOutTitle: 'Odjaviti {{name}}?',
    checkOutDefaultName: 'ovog posjetitelja',
    checkOutConfirmLabel: 'Odjavi i opozovi njihove kartice',
    checkOutBody1: 'Svaka kartica još aktivna na ovom posjetu odmah se <0>opoziva</0>, a svaki pristupnik je obaviješten, tako da kartica prestaje otvarati vrata čak i bez povezanosti.',
    checkOutBody2: 'Zapisi kartica <0>nisu</0> umirovljeni. Umirovljenje oslobađa UID za sljedećeg nositelja, a pult to radi kad karticu ponovno izda — što čuva pristup ovog posjetitelja u revizijskom tragu.',
    cancelVisitTitle: 'Otkazati ovaj posjet?',
    cancelVisitConfirmLabel: 'Otkaži posjet',
    cancelVisitBody: '{{name}} se neće očekivati. Otkazivanje se odbija nakon što je kartica izdana — umjesto toga odjavite posjetitelja.',
    cancelVisitDefaultName: 'Posjetitelj',
    denyTitle: 'Odbiti {{name}}?',
    denyConfirmLabel: 'Odbij — blokira samo nove posjete',
    denyReasonPlaceholder: 'Razlog (neobavezno)',
    denyBody1: 'Novi posjeti i nova izdavanja kartica bit će odbijeni za ovu osobu.',
    denyBody2: '<0>Ovo ništa ne opoziva.</0> Kartica koju ova osoba već drži nastavlja raditi. Ako je posjeduju, opozovite je i na stranici <1>Kartice</1> — oznaka odbijanja se nikad ne čita na vratima.',
    eraseTitle: 'Izbrisati {{name}}?',
    eraseConfirmLabel: 'Izbriši',
    eraseBody: 'Njihovi posjeti se brišu, a njihovo ime se uklanja iz umirovljenih zapisa kartica. Povijest vrata i kartica se čuva. Odbija se dok drže karticu koja nije umirovljena.',
    confirmBack: 'Natrag',

    editPersonTitle: 'Uredi osobu',
    registerPersonTitle: 'Prijavi osobu',
    personDialogDescription: 'Posjetitelj je osoba; posjet je jedna omeđena prigoda za koju se očekuje.',
    fullNameLabel: 'Ime i prezime',
    fullNamePlaceholder: 'Ana Horvat',
    companyLabel: 'Tvrtka',
    companyPlaceholder: 'Izvođač ili poslodavac',
    phoneLabel: 'Telefon',
    emailLabel: 'E-pošta',
    idRefLabel: 'Referenca provjere identiteta',
    idRefPlaceholder: 'npr. provjereno prema putovnici, ref. 2026-0912',
    idRefHint: 'Referenca na provjeru obavljenu na pultu — nikad broj dokumenta, slika ili sken. To su osobni podaci i uklanjaju se kad se osoba izbriše.',
    notesLabel: 'Napomene',
    registerAction: 'Prijavi',

    editVisitTitle: 'Uredi posjet',
    registerVisitTitle: 'Prijavi posjet',
    editVisitDescription: 'Promjena ovoga ne mijenja karticu koja je već izdana: kartica nosi svoje vlastito potpisano razdoblje.',
    registerVisitDescription: 'Kartica izdana za ovaj posjet preuzima ova vremena kao svoje potpisano razdoblje valjanosti, osim ako ih pult ne suzi.',
    visitorLabel: 'Posjetitelj',
    whoIsComingPlaceholder: 'Tko dolazi?',
    deniedSuffix: ' (odbijen)',
    hostLabel: 'Domaćin',
    hostPlaceholder: 'Tko ih očekuje',
    purposeLabel: 'Svrha',
    purposePlaceholder: 'Održavanje, razgovor, dostava…',
    expectedFromLabel: 'Očekivano od',
    expectedUntilLabel: 'Očekivano do',
    overstayingWarning: '<0>Ovaj posjetitelj predugo se zadržava.</0> Produljenje posjeta bilježi novo očekivanje. Ne produljuje njihovu karticu.',
  },
};
